import {
  Banner,
  BlockStack,
  Button,
  CalloutCard,
  Card,
  ChoiceList,
  Collapsible,
  Form,
  FormLayout,
  Frame,
  Icon,
  InlineStack,
  Layout,
  Modal,
  Page,
  ProgressBar,
  ResourceItem,
  ResourceList,
  Text,
  TextField,
} from "@shopify/polaris"
import {
  BlogSettingsSave,
  ConnectionAction,
  ConnectionStatus,
  ConnectionTrigger,
  Endpoint,
  HomeStateSync,
  PageRoute,
  ShopFrontend,
  SyncDirection,
} from "../../typings/types"
import type {
  ConnectionFrontend,
  SyncEvent,
  SyncPayload,
} from "../../typings/types"
import React, { useContext, useEffect, useState } from "react"
import { ShopContext, userLoggedInFetch } from "../App"
import { Trans, useTranslation } from "react-i18next"
import { modifyConnectionUrl, syncUrl } from "../helpers/api"
import { readJsonLines } from "../helpers/stream"
import { useAppBridge } from "@shopify/app-bridge-react"
import { useLocation, useNavigate } from "react-router-dom"

import BlogSettings from "./BlogSettings"
import ReleaseBanner from "./ReleaseBanner"
import SyncPreviewList from "./SyncPreviewList"
import { DeadSimpleIcon } from "./Icons"
import { capitalize } from "../helpers/misc"
import { RELEASES, releaseToShow } from "../helpers/releases"
import {
  recordReleaseView,
  releaseViewsOnDevice,
} from "../helpers/release-views"
import { spotlight } from "../helpers/spotlight"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import relativeTime from "dayjs/plugin/relativeTime"

// var relativeTime = require("dayjs/plugin/relativeTime")
dayjs.extend(relativeTime)
dayjs.extend(duration)

const TIMEOUT_IN_MS = 5000

const filterInvites = (connections: ConnectionFrontend[]) =>
  connections.filter(({ status }) => status === ConnectionStatus.RECEIVED)
const filterPending = (connections: ConnectionFrontend[]) =>
  connections.filter(({ status }) => status === ConnectionStatus.PENDING)
const filterNonInvites = (connections: ConnectionFrontend[]) =>
  connections.filter(({ status }) => status !== ConnectionStatus.RECEIVED)
const filterActiveShops = (connections: ConnectionFrontend[]) =>
  connections.filter(({ status }) => status === ConnectionStatus.ACTIVE)

const Index = () => {
  const { t, i18n } = useTranslation(["common", "home"])
  const navigate = useNavigate()
  const location = useLocation()
  const app = useAppBridge()
  const [suggestionsVisible, setSuggestionsVisible] = useState(false)
  const fetch = userLoggedInFetch(app)
  const [shop, setShop] = useContext(ShopContext)
  const [sendingInvite, setInviteLoading] = useState(false)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [shopRowsLoading, setShopRowsLoading] = useState(false)
  const [blogModalActive, setBlogModalActive] = useState<{
    domain: undefined | string
    name?: string
    disconnectFunc?: () => void
  }>({ domain: undefined })
  const [modalActive, setModalActive] = useState<{
    children: any
    title: string
    action: {
      content: string
      onAction: () => void
    }
    secondaryAction?: {
      content: string
      onAction: () => void
    }
  }>()
  const [instructionsVisible, setInstructionsVisible] = useState(false)
  const [inviteError, setInviteError] = useState("")
  const [inviteDirection, setInviteDirection] = useState<SyncDirection>(
    SyncDirection.TO
  )
  const [inviteButtonText, setInviteButtonText] = useState<string>()
  const [shopDomain, setShopDomain] = useState("")
  const [syncStatus, setSyncStatus] = useState<Record<string, string>>({})

  // App Bridge toasts show at the top of the admin frame, where Sidekick's
  // bottom-centre panel can't cover them
  useEffect(() => {
    if (!shop?.session_restored) return
    app.toast.show(t("home:sync.reconnected"))
    setShop((prev) => prev && { ...prev, session_restored: false })
  }, [shop?.session_restored])

  // runs when component mounts
  useEffect(() => {
    if (!!shop) {
      const { connections, domain } = shop
      // the first-run screen keeps the instructions collapsed below the
      // connect form; once a request is out they open until it is accepted
      if (
        connections.length > 0 &&
        filterActiveShops(connections).length === 0
      ) {
        setInstructionsVisible(true)
      }
      setShopDomain(domain)
    }
  }, [shop])

  const [helpText, setHelpText] = useState<string>()
  const copyShopName = async (e?: React.FocusEvent<Element>) => {
    e?.preventDefault()
    const shopNameInput: HTMLInputElement | null = document.querySelector(
      "input[name='shopName']"
    )
    if (shopNameInput) shopNameInput.select()
    if (!e || (e.target as Element).tagName !== "INPUT") {
      setTimeout(resetShopName, TIMEOUT_IN_MS)
    }
    try {
      await navigator.clipboard.writeText(shopDomain)
    } catch (err) {
      document.execCommand("copy")
    }

    setHelpText(t("home:connectShop.content.copied"))
  }
  const resetShopName = () => {
    setHelpText(undefined)
  }

  const [inviteeDomain, setInviteeDomain] = useState("")
  const { connections: connectedShops } = shop || { connections: [] }

  useEffect(() => {
    const { state } = location as {
      state: HomeStateSync
    }

    if (state && state.syncConfirmation) {
      if (state.newlyAutomatic) {
        // getting to home from payment confirmation for automatic syncing
        setModalActive({
          title: t("home:initialSync.title"),
          children: (
            <BlockStack gap="200">
              <p>{t("home:initialSync.successful")}</p>
              <p>
                <Trans i18nKey="home:initialSync.syncAll.message">
                  <Text as="span" fontWeight="semibold">
                    If you want to sync all blog posts
                  </Text>{" "}
                  (including already existing posts, if they exist) to newly
                  created blogs on the child shop, click
                </Trans>{" "}
                <code>{t("home:initialSync.syncAll.action")}</code>.
              </p>
              <p>
                <Trans i18nKey="home:initialSync.settings.message">
                  Otherwise,{" "}
                  <Text as="span" fontWeight="semibold">
                    if you want to pick specific blogs to sync, want to pick
                    existing child blogs to sync to, and/or only want to sync
                    posts created after a certain date
                  </Text>
                  , click
                </Trans>{" "}
                <code>{t("home:initialSync.settings.action")}</code>.
              </p>
            </BlockStack>
          ),
          action: {
            content: t("home:initialSync.syncAll.action"),
            onAction: () => {
              manualSync(state.syncDomain)
              setModalActive(undefined)
              app.toast.show(t("home:initialSync.blogsBeingSynced"))
            },
          },
          secondaryAction: {
            content: t("home:initialSync.settings.action"),
            onAction: () => {
              setBlogModalActive({
                domain: state.syncDomain,
                disconnectFunc: async () =>
                  await modifyConnection(
                    state.syncDomain,
                    SyncDirection.TO,
                    ConnectionAction.DELETE
                  ),
              })
              setModalActive(undefined)
            },
          },
        })
      } else {
        // getting to home from confirming payment on a manual sync
        manualSync(
          state.syncDomain,
          false,
          state.forceUpdate,
          state.selectedAt
            ? {
                selectedAt: state.selectedAt,
                confirmCount: state.confirmCount,
              }
            : undefined
        )
      }
    }
  }, [location])

  useEffect(() => {
    if (inviteeDomain === shopDomain) {
      setInviteError(t("home:inviteErrors.shopToItself"))
    } else if (
      filterNonInvites(connectedShops).some((s) => s.domain == inviteeDomain)
    ) {
      setInviteError(t("home:inviteErrors.already"))
    } else if (
      filterInvites(connectedShops).some((s) => s.domain == inviteeDomain)
    ) {
      setInviteError(t("home:inviteErrors.inRequests"))
    } else if (inviteError) setInviteError("")
  }, [inviteeDomain, shopDomain])

  const manualSync = async (
    syncDomain: string,
    preview?: boolean,
    forceUpdate?: boolean,
    // The selection the merchant was shown: on a preview it asks for that
    // selection's charge, after the payment it pins what the sync copies
    selection?: {
      selectedAt: string
      confirmCount?: number
      signature?: string
    }
  ) => {
    if (
      connectedShops.length === 0 ||
      !connectedShops.some((shop) => shop.domain === syncDomain)
    )
      return
    setShopRowsLoading(true)
    const syncParams: SyncPayload = {
      sync_domain: syncDomain,
      forceUpdate: forceUpdate ? "true" : undefined,
    }
    if (selection) {
      syncParams.selectedAt = selection.selectedAt
      if (selection.confirmCount !== undefined)
        syncParams.confirmCount = `${selection.confirmCount}`
      if (selection.signature) syncParams.signature = selection.signature
    }
    if (preview) {
      setSyncStatus({
        [syncDomain]: t("home:sync.fetchingMessage"),
      })
      syncParams.previewPricing = true
    }

    const results: Exclude<SyncEvent, { type: "status" }>[] = []
    try {
      const response = await fetch(syncUrl(syncParams))
      if (response?.status === 409) {
        const body = await response.json().catch(() => undefined)
        setSyncStatus({
          [syncDomain]:
            body?.error === "reauth_required"
              ? t("home:sync.reauthStore", { store: body.shop })
              : t("home:sync.failed"),
        })
        setShopRowsLoading(false)
        return
      }
      if (response?.body)
        await readJsonLines<SyncEvent>(response.body, (event) => {
          if (event.type === "status")
            setSyncStatus({ [syncDomain]: event.message })
          else results.push(event)
        })
    } catch (err) {
      console.error("Sync response could not be read:", err)
    }
    setShopRowsLoading(false)

    // the stream ending without a result means the sync never reported back
    const result = results.pop()
    if (!result) {
      setSyncStatus({ [syncDomain]: t("home:sync.failed") })
      return
    }
    if (result.type === "error") {
      setSyncStatus({
        [syncDomain]: t("home:sync.errorWithRef", { ref: result.sentry_id }),
      })
      return
    }

    const { preview: syncPreview, confirmation_url } = result
    if (syncPreview) {
      setSyncStatus({})
      setModalActive({
        title: t("home:sync.preview.title"),
        children: (
          <SyncPreviewList preview={syncPreview} refreshed={!!selection} />
        ),
        action: {
          content: t("home:sync.preview.confirm"),
          onAction: () => {
            setModalActive(undefined)
            manualSync(syncDomain, true, forceUpdate, {
              selectedAt: syncPreview.selected_at,
              confirmCount: syncPreview.count,
              signature: syncPreview.signature,
            })
          },
        },
      })
      return
    }
    if (confirmation_url) {
      window.open(confirmation_url, "_top")
      return
    }

    // update the stored last sync
    setShop((prevShop) => {
      const { connections } = prevShop!
      const existing = connections.find((shop) => shop.domain === syncDomain)
      if (existing)
        existing.last_sync = {
          articles_synced: result.articles_synced,
          date: result.date,
        } as ConnectionFrontend["last_sync"]

      return {
        ...prevShop,
        connections: [...connections],
      } as ShopFrontend
    })

    setTimeout(() => setSyncStatus({}), 10000)
  }

  const modifyConnection = async (
    domain: string,
    direction: SyncDirection,
    action: ConnectionAction
  ) => {
    if (action === ConnectionAction.INVITE) {
      if (!/^([a-z0-9\-]+\.){1,}[a-z]+$/i.test(domain)) {
        setInviteError(t("home:inviteErrors.invalidDomain"))
        return
      }
      if (!/^([a-z0-9\-]+\.){1,}myshopify\.com$/i.test(domain)) {
        setInviteError(t("home:inviteErrors.notShopifyDomain"))
        return
      }
    }
    let fetchUrl = modifyConnectionUrl({ domain, direction })
    const request_method = {
      [ConnectionAction.ACCEPT]: "PATCH",
      [ConnectionAction.DELETE]: "DELETE",
      [ConnectionAction.INVITE]: "POST",
    }

    if (action === ConnectionAction.INVITE) setInviteLoading(true)
    else if (action === ConnectionAction.ACCEPT) setAcceptLoading(true)
    else setShopRowsLoading(true)

    // when creating or accepting a connection and this is the child shop,
    // take user to pricing page
    if (action !== ConnectionAction.DELETE) {
      // setInstructionsVisible(false)
      if (direction === SyncDirection.FROM) {
        let pricingUrl = "/pricing"
        if (action === ConnectionAction.INVITE)
          pricingUrl += `?invite=${domain}`
        navigate(pricingUrl)
        return
      }
    }

    let requestStatus: number | undefined
    try {
      const res = await fetch(fetchUrl, {
        method: request_method[action],
      })
      if (!res) return
      if (!res.ok) {
        return res.text().then((text) => {
          setInviteError(text)
          setInviteLoading(false)
        })
      }
      requestStatus = res.status // res.json();
    } catch (err) {
      setInviteError(t("home:inviteErrors.generalError"))
      setInviteLoading(false)
      return
    }

    if (requestStatus === 200) {
      if (action === ConnectionAction.INVITE) {
        setInviteeDomain("")
        setInviteButtonText("Sent!")
        setTimeout(() => setInviteButtonText(undefined), TIMEOUT_IN_MS)
        setInviteLoading(false)

        // add the invited shop to the list of connected shops as Pending
        setShop((prevShop) => {
          const { connections } = prevShop!
          return {
            ...prevShop,
            connections: [
              ...connections,
              { domain, direction, status: ConnectionStatus.PENDING },
            ],
          } as ShopFrontend
        })
      } else if (action === ConnectionAction.ACCEPT) {
        setAcceptLoading(false)

        // change the status of the accepted shop to active
        setShop((prevShop) => {
          const { connections } = prevShop!
          const existing = connections.find((c) => c.domain === domain)
          if (existing) existing.status = ConnectionStatus.ACTIVE
          return {
            ...prevShop,
            connections: [...connections],
          } as ShopFrontend
        })
      } else {
        setShopRowsLoading(false)

        setShop((prevShop) => {
          const { connections } = prevShop!
          return {
            ...prevShop,
            connections: connections.filter((c) => c.domain !== domain),
          } as ShopFrontend
        })
      }
    }
  }

  // get time of next sync
  const now = dayjs()
  const next = new Date()
  const utcOffset = now.utcOffset() / 60
  const syncTime = 12 + utcOffset
  if (next.getHours() < syncTime) {
    // if it's before the first sync time
    next.setHours(syncTime)
  } else {
    const secondSync = syncTime + 12
    if (secondSync <= now.hour()) {
      // if it's after the second sync time
      next.setHours(syncTime)
      next.setDate(next.getDate() + 1)
    } else {
      next.setHours(secondSync % 24)
      if (secondSync > 23) next.setDate(next.getDate() + 1)
    }
  }
  next.setMinutes(0)
  next.setMilliseconds(0)
  const nextSync = dayjs(next)
  const timeToSync = now.to(nextSync) // dayjs.duration(nextSync.diff(now))

  const confirmedConnectionsExist = false

  const handleSyncSettings = async (
    domain: string,
    settings?: BlogSettingsSave
  ) => {
    if (!settings) return
    const blogs = { ...settings.blogs }
    for (const id of Object.keys(blogs)) {
      // delete any unchanged blog settings
      if (!blogs[id].newInfo) delete blogs[id]
    }
    if (Object.keys(blogs).length < 1 && settings.ignored_tags === undefined)
      return
    setShopRowsLoading(true)
    try {
      const response = await fetch(Endpoint.BLOGS + `?domain=${domain}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...settings, blogs }),
      })
      app.toast.show(
        t(response.ok ? "home:settings.success" : "home:settings.failed"),
        { isError: !response.ok }
      )
    } catch {
      app.toast.show(t("home:settings.failed"), { isError: true })
    } finally {
      setShopRowsLoading(false)
    }
  }

  // useEffect(() => {
  //   // Identify user for Help Scout
  //   const loadChat = async () => {
  //     const { domain, hmac, email, name } = await fetch("/hmac").then((res) =>
  //       res?.json()
  //     )

  //     window.Beacon("identify", {
  //       name: name || domain,
  //       email: email || "",
  //       domain,
  //       signature: hmac,
  //     })
  //   }

  //   !!window.Beacon && loadChat()

  //   return () => {}
  // }, [])

  // const setInvite = (val: string) => {
  //   const matches = val.match(/((?:[a-z\-]+\.){1,}[a-z]+)/i)
  //   if (matches && matches.length > 1) setInviteeDomain(matches[1])
  //   else setInviteeDomain(val)
  // }

  const invited_shops = filterPending(connectedShops)
  const non_invited_shops = filterNonInvites(connectedShops)
  const active_shops = filterActiveShops(connectedShops)
  const isParent =
    connectedShops.length > 0 &&
    connectedShops[0].direction === SyncDirection.TO
  const hasActiveParent =
    !isParent && active_shops.length > 0 && active_shops[0]
  const expiringSubscription =
    hasActiveParent &&
    hasActiveParent.trigger === ConnectionTrigger.AUTOMATIC &&
    shop?.billing_active === false
  const allAutomaticChildren =
    isParent &&
    active_shops.length > 0 &&
    active_shops.every(({ trigger }) => trigger === ConnectionTrigger.AUTOMATIC)
  const allManualChildren =
    !allAutomaticChildren &&
    active_shops.length > 0 &&
    active_shops.every(({ trigger }) => trigger === ConnectionTrigger.MANUAL)

  const listedShops = [...active_shops, ...invited_shops]
  const reauthMessageFor = (item: ConnectionFrontend) => {
    // the shop this row represents vs. the current shop
    const rowShopAuth =
      item.direction === SyncDirection.TO
        ? item.child_auth_status
        : item.parent_auth_status
    const ownShopAuth =
      item.direction === SyncDirection.TO
        ? item.parent_auth_status
        : item.child_auth_status
    if (rowShopAuth === "reauth_required") return t("home:sync.reauthThisStore")
    if (ownShopAuth === "reauth_required")
      return t("home:sync.reauthStore", { store: shopDomain })
    if (rowShopAuth === "recovery") return t("home:sync.recoveryThisStore")
    if (ownShopAuth === "recovery")
      return t("home:sync.recoveryStore", { store: shopDomain })
    return undefined
  }
  const nextBannerVisible =
    non_invited_shops.length > 0 &&
    (active_shops.length === 0 ||
      active_shops.some((s) => s.trigger === ConnectionTrigger.AUTOMATIC))
  // a received request is not first run: accepting it is the way forward
  const firstRun = connectedShops.length === 0

  const openBlogSettings = ({ domain, name, direction }: ConnectionFrontend) =>
    setBlogModalActive({
      domain,
      name,
      disconnectFunc: async () =>
        await modifyConnection(domain, direction, ConnectionAction.DELETE),
    })

  // Snapshot at mount so recording this view cannot hide the banner mid-visit
  const [releaseDeviceViews] = useState(() =>
    releaseViewsOnDevice(RELEASES[0]?.id)
  )
  const release = releaseToShow(shop?.dismissed_release, {
    hasConnections: !firstRun,
    nextBannerVisible,
    reauthWarningVisible: listedShops.some((item) => !!reauthMessageFor(item)),
    deviceViews: releaseDeviceViews,
  })
  useEffect(() => {
    if (release) recordReleaseView(release.id)
  }, [release?.id])
  const dismissRelease = async (id: string) => {
    setShop((prev) => prev && { ...prev, dismissed_release: id })
    try {
      const response = await fetch(Endpoint.RELEASE_DISMISS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ release: id }),
      })
      if (!response.ok)
        console.error(`Release dismissal responded ${response.status}`)
    } catch (err) {
      console.error("Release dismissal failed:", err)
    }
  }

  const connectForm = (
    <BlockStack gap="400">
      <Form
        noValidate
        onSubmit={async () =>
          await modifyConnection(
            inviteeDomain,
            inviteDirection,
            ConnectionAction.INVITE
          )
        }
      >
        <FormLayout>
          <TextField
            value={inviteeDomain}
            onChange={setInviteeDomain}
            label={t("home:connectShop.content.inviteeLabel")}
            type="url"
            inputMode="url"
            autoComplete="off"
            error={inviteError}
          />
          <ChoiceList
            title={t("home:connectShop.content.directionLabel")}
            choices={[
              {
                label: t("home:syncTo"),
                value: SyncDirection.TO,
                helpText: t("home:connectShop.content.toHelp"),
              },
              {
                label: t("home:syncFrom"),
                value: SyncDirection.FROM,
                disabled: connectedShops.length > 0,
                helpText:
                  connectedShops.length > 0
                    ? t("home:connectShop.content.fromWarning")
                    : t("home:connectShop.content.fromHelp"),
              },
            ]}
            selected={[inviteDirection]}
            onChange={([direction]) =>
              setInviteDirection(direction as SyncDirection)
            }
            allowMultiple={false}
          />
          <Button
            variant={
              firstRun || inviteeDomain.length > 0 ? "primary" : undefined
            }
            disabled={inviteError.length > 0 || !!inviteButtonText}
            loading={sendingInvite}
            submit
          >
            {inviteButtonText || t("home:inviteButtonText")}
          </Button>
        </FormLayout>
      </Form>
      <TextField
        label={t("home:connectShop.content.domainLabel")}
        value={shopDomain}
        labelAction={{
          content: t("home:connectShop.content.clickCopy"),
          onAction: copyShopName,
        }}
        type="text"
        readOnly
        monospaced
        autoComplete="off"
        name="shopName"
        onFocus={copyShopName}
        onBlur={resetShopName}
      />
    </BlockStack>
  )

  return (
    <Frame>
      <Page>
        <Layout>
          {release && (
            <Layout.Section>
              <ReleaseBanner
                release={release}
                onDismiss={() => dismissRelease(release.id)}
                onShowConnection={
                  active_shops.length > 0
                    ? () =>
                        spotlight(
                          document.querySelector(
                            `[data-connection="${active_shops[0].domain}"]`
                          )
                        )
                    : undefined
                }
              />
            </Layout.Section>
          )}
          {firstRun && (
            <Layout.Section>
              <Card>
                <BlockStack gap="500">
                  <BlockStack gap="200">
                    <Text variant="headingLg" as="h2">
                      {t("home:firstRun.title")}
                    </Text>
                    <Text as="p">{t("home:firstRun.message")}</Text>
                  </BlockStack>
                  {connectForm}
                  <Text as="p" tone="subdued">
                    {t("home:firstRun.singleStore")}
                  </Text>
                </BlockStack>
              </Card>
            </Layout.Section>
          )}
          {hasActiveParent && !expiringSubscription && (
            <Layout.Section>
              <Card>
                <InlineStack
                  align="space-between"
                  blockAlign="center"
                  gap="400"
                >
                  <Text as="span">
                    {hasActiveParent.trigger === ConnectionTrigger.AUTOMATIC ? (
                      <>
                        {t("home:switch.messageAuto")}{" "}
                        <Text as="span" fontWeight="semibold">
                          {t("home:automatically")}
                        </Text>
                        .
                      </>
                    ) : (
                      t("home:switch.messageManual")
                    )}
                  </Text>
                  <Button onClick={() => navigate(PageRoute.PRICING)}>
                    {hasActiveParent.trigger === ConnectionTrigger.AUTOMATIC
                      ? t("home:switch.toManual")
                      : t("home:switch.toAutomatic")}
                  </Button>
                </InlineStack>
              </Card>
            </Layout.Section>
          )}
          {isParent && active_shops.length > 0 && (
            <Layout.Section>
              <Card>
                <BlockStack gap="200">
                  {(allAutomaticChildren || allManualChildren) && (
                    <Text variant="headingMd" as="h3">
                      {allAutomaticChildren
                        ? t("home:switch.parent.titleAutomatic")
                        : t("home:switch.parent.titleManual")}
                    </Text>
                  )}
                  <p>
                    {allAutomaticChildren
                      ? t("home:switch.parent.messageAutomatic")
                      : allManualChildren
                      ? t("home:switch.parent.messageManual")
                      : t("home:switch.parent.messageMixed")}
                  </p>
                  <Text as="span" tone="subdued">
                    {allManualChildren
                      ? t("home:switch.parent.changeAutomatic")
                      : t("home:switch.parent.changeManual")}
                  </Text>
                </BlockStack>
              </Card>
            </Layout.Section>
          )}
          {/* <button onClick={syncBlogs}>Sync blogs</button> */}
          {filterInvites(connectedShops).length > 0 && (
            <Layout.Section>
              {filterInvites(connectedShops).map(({ direction, domain }) => {
                return (
                  <CalloutCard
                    key={`invite-${domain}`}
                    // illustration="https://s3.us-west-2.amazonaws.com/copyblogposts.aurnik.com/assets/Shopify+App+Icon-bw.png"
                    primaryAction={{
                      content: t("home:connectionRequest.accept"),
                      // @ts-ignore
                      primary: true,
                      // @ts-ignore
                      loading: acceptLoading,
                      disabled: shopRowsLoading,
                      onAction: async () =>
                        await modifyConnection(
                          domain,
                          direction,
                          ConnectionAction.ACCEPT
                        ),
                    }}
                    secondaryAction={{
                      content: t("home:connectionRequest.decline"),
                      // @ts-ignore
                      loading: shopRowsLoading,
                      disabled: acceptLoading,
                      destructive: true,
                      onAction: async () =>
                        await modifyConnection(
                          domain,
                          direction,
                          ConnectionAction.DELETE
                        ),
                    }}
                    title={t("home:connectionRequest.title")}
                  >
                    <p>
                      {direction === SyncDirection.TO
                        ? t("home:connectionRequest.messageTo")
                        : t("home:connectionRequest.messageFrom")}
                      <br />
                      <Text as="span" fontWeight="semibold">
                        {domain}
                      </Text>
                    </p>
                  </CalloutCard>
                )
              })}
            </Layout.Section>
          )}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd" as="h3">
                    {t("home:instructions.title")}
                  </Text>
                  <Button
                    variant="plain"
                    onClick={() => setInstructionsVisible(!instructionsVisible)}
                  >
                    {instructionsVisible ? t("common:hide") : t("common:show")}
                  </Button>
                </InlineStack>
                <Collapsible
                  open={instructionsVisible}
                  id="basic-collapsible"
                  transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                  }}
                  expandOnPrint
                >
                  <BlockStack gap="400">
                    <BlockStack gap="200">
                      {t("home:instructions.message")}
                    </BlockStack>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h4">
                        {t("home:instructions.directions.title")}
                      </Text>
                      <BlockStack gap="100">
                        <code>{t("home:syncTo")}</code>
                        {t("home:instructions.directions.parent")}
                      </BlockStack>
                      <BlockStack gap="100">
                        <code>{t("home:syncFrom")}</code>
                        {t("home:instructions.directions.child")}
                      </BlockStack>
                    </BlockStack>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h4">
                        {t("home:instructions.methods.title")}
                      </Text>
                      <BlockStack gap="100">
                        <code>{t("home:automatic")}</code>
                        {t("home:instructions.methods.automatic")}
                      </BlockStack>
                      <BlockStack gap="100">
                        <code>{t("home:manual")}</code>
                        {t("home:instructions.methods.manual")}
                      </BlockStack>
                    </BlockStack>
                    <BlockStack gap="200">
                      {t("home:instructions.settings")}
                    </BlockStack>
                  </BlockStack>
                </Collapsible>
              </BlockStack>
            </Card>
          </Layout.Section>
          {non_invited_shops.length > 0 && (
            <>
              {nextBannerVisible && (
                <Layout.Section>
                  <Banner tone="info" title={t("home:next.title")}>
                    <p>
                      {active_shops.length > 0
                        ? t("home:next.message", {
                            syncTime: nextSync.toDate(),
                            timeToSync: timeToSync,
                            formatParams: { syncTime: { timeStyle: "short" } },
                          }) +
                          (expiringSubscription
                            ? t("home:next.expiring", {
                                d: dayjs(shop?.billing_last)
                                  .add(30, "days")
                                  .toDate(),
                                formatParams: {
                                  d: {
                                    weekday: "long",
                                    month: "long",
                                    day: "numeric",
                                  },
                                },
                              })
                            : "")
                        : invited_shops.length === 1
                        ? t("home:next.acceptTheInvite", {
                            val: invited_shops[0].domain,
                          })
                        : t("home:next.acceptAnInvite")}
                    </p>
                  </Banner>
                </Layout.Section>
              )}

              <Layout.AnnotatedSection
                id="connectedShops"
                title={capitalize(
                  isParent ? t("home:syncTo") : t("home:syncFrom")
                )}
                description={t("home:connectedShops.description")}
              >
                <Card>
                  <ResourceList
                    resourceName={{
                      singular: t(
                        "home:connectedShops.content.resource.name.singular"
                      ),
                      plural: t(
                        "home:connectedShops.content.resource.name.plural"
                      ),
                    }}
                    items={listedShops}
                    renderItem={(item) => {
                      const {
                        domain,
                        name,
                        direction,
                        last_sync,
                        status,
                        trigger,
                      } = item

                      const reauthMessage = reauthMessageFor(item)

                      return (
                        // @ts-ignore
                        <ResourceItem
                          verticalAlignment="center"
                          accessibilityLabel={t("home:shopRow.changeSettings")}
                        >
                          <div data-connection={domain}>
                            <InlineStack
                              align="space-between"
                              blockAlign="center"
                              gap="400"
                            >
                              <BlockStack gap="100">
                                <h3>
                                  <Text as="span" fontWeight="semibold">
                                    {name || domain}
                                  </Text>
                                  {trigger
                                    ? ` (${
                                        trigger === ConnectionTrigger.AUTOMATIC
                                          ? t("home:automatic")
                                          : t("home:manual")
                                      })`
                                    : ""}
                                </h3>
                                {status === ConnectionStatus.PENDING ? (
                                  <Text as="span">
                                    {t("home:shopRow.waiting")}
                                  </Text>
                                ) : (
                                  <Text as="span" tone="subdued">
                                    {syncStatus[domain] ||
                                      (last_sync ? (
                                        <>
                                          {t("home:lastSynced")}{" "}
                                          {now.to(last_sync.date)} (
                                          {last_sync.articles_synced}{" "}
                                          {last_sync.articles_synced !== 1
                                            ? t("home:postPlural")
                                            : t("home:post")}
                                          )
                                        </>
                                      ) : (
                                        t("home:notSynced")
                                      ))}
                                  </Text>
                                )}
                              </BlockStack>
                              {status === ConnectionStatus.ACTIVE && (
                                <InlineStack gap="200">
                                  <Button
                                    variant="secondary"
                                    loading={shopRowsLoading}
                                    onClick={async () =>
                                      await manualSync(
                                        domain,
                                        trigger !== ConnectionTrigger.AUTOMATIC
                                      )
                                    }
                                  >
                                    {t(
                                      "home:connectedShops.content.resource.actions.sync"
                                    )}
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    loading={shopRowsLoading}
                                    onClick={() => openBlogSettings(item)}
                                  >
                                    {t(
                                      "home:connectedShops.content.resource.actions.settings"
                                    )}
                                  </Button>
                                </InlineStack>
                              )}
                              {status === ConnectionStatus.PENDING && (
                                <Button
                                  variant="secondary"
                                  tone="critical"
                                  loading={shopRowsLoading}
                                  onClick={async () =>
                                    await modifyConnection(
                                      domain,
                                      direction,
                                      ConnectionAction.DELETE
                                    )
                                  }
                                >
                                  {t("home:shopRow.deleteRequest")}
                                </Button>
                              )}
                            </InlineStack>
                          </div>
                          {reauthMessage && (
                            <div style={{ marginTop: "0.5rem" }}>
                              <Banner tone="warning">{reauthMessage}</Banner>
                            </div>
                          )}
                        </ResourceItem>
                      )
                    }}
                  />
                </Card>
              </Layout.AnnotatedSection>
            </>
          )}
          {!firstRun &&
            !connectedShops.some(
              ({ direction }) => direction === SyncDirection.FROM
            ) && (
              <Layout.AnnotatedSection
                id="connectShop"
                title={t("home:connectShop.title")}
                description={t("home:connectShop.description")}
              >
                <Card>{connectForm}</Card>
              </Layout.AnnotatedSection>
            )}
        </Layout>
        <BlogSettings
          active={blogModalActive.domain}
          name={blogModalActive.name}
          handleDisconnect={() => {
            if (blogModalActive.disconnectFunc) blogModalActive.disconnectFunc()
            setBlogModalActive({ domain: undefined })
          }}
          handleClose={(settings?: BlogSettingsSave) => {
            handleSyncSettings(blogModalActive.domain as string, settings)
            setBlogModalActive({ domain: undefined })
          }}
          forceSync={() => {
            if (!blogModalActive.domain) return
            const trigger = active_shops.find(
              (shop) => shop.domain === blogModalActive.domain
            )?.trigger
            manualSync(
              blogModalActive.domain,
              trigger !== ConnectionTrigger.AUTOMATIC,
              true
            )
            setBlogModalActive({ domain: undefined })
          }}
        />
        <Modal
          open={!!modalActive}
          onClose={() => setModalActive(undefined)}
          title={modalActive?.title}
          primaryAction={modalActive?.action}
          secondaryActions={
            modalActive?.secondaryAction
              ? [
                  {
                    content: t("common:cancel"),
                    onAction: () => setModalActive(undefined),
                  },
                  modalActive?.secondaryAction,
                ]
              : [
                  {
                    content: t("common:cancel"),
                    onAction: () => setModalActive(undefined),
                  },
                ]
          }
        >
          <Modal.Section>{modalActive?.children}</Modal.Section>
        </Modal>
        <div style={{ display: "flex", margin: "2rem 0" }}>
          <Icon source={DeadSimpleIcon} tone="subdued" />
        </div>
        {/* <SubTest /> */}
      </Page>
    </Frame>
  )
}

export default Index
